import { Component, Input, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TranslateModule } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { DacService } from './dac.service';
import { DataTableBuilderComponent } from '../../components/data-table-builder/data-table-builder.component';
import { StepsModule } from 'primeng/steps';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { CalendarModule } from 'primeng/calendar';
import { CheckboxModule } from 'primeng/checkbox';
import { TableModule } from 'primeng/table';
import { InputNumberModule } from 'primeng/inputnumber';
import { DividerModule } from 'primeng/divider';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { DataTableAction, DataTableRowActions, DynamicDataTable } from '@/components/data-table-builder/data-table.types';
import { Router } from '@angular/router';
import { getDacDataTableStructure } from './utils/dac-datatable-object';
import { DialogComponent } from '@/components/dialog/dialog.component';
import { DialogObject } from '@/components/dialog/dialog.types';
import { getDacPreCreateDialogObject } from './utils/dac-pre-create-dialog-object';
import { SelectOption } from '@/components/form-builder/form-builder.types';
import { getDACPreCreateFormObject } from './utils/dac-create-dialog-form-object';
import { DacRepository } from '@/store/dac-state/dac-state.repository';
import { DacCreateDto, DacResponse } from '@/types';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { TasksStatus } from '@/types/dac-responses.types';
import { AuthorizationService } from '@/services/authorization.service';
import { DynaDocsModelsService } from '../settings/dyna-docs-models/dyna-docs-models.service';
import { getFormBuilderObjectFactory } from '@/components/form-builder/utils/form-builder-factory';
import { getDatatableBuilderObjectFactory } from '@/components/data-table-builder/utils/datatable-builder-factory';
import { dacPreCreateDraftValidationSchema } from '@/validations/dac/dac.validation';
import { SubmitBiddersDialogComponent } from './submit-bidders-dialog/submit-bidders-dialog.component';

@Component({
  selector: 'sm-dac',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    TranslateModule,
    DataTableBuilderComponent,
    StepsModule,
    CardModule,
    InputTextModule,
    CalendarModule,
    CheckboxModule,
    TableModule,
    InputNumberModule,
    DividerModule,
    TooltipModule,
    TagModule,
    DialogComponent,
  ],
  templateUrl: './dac.component.html',
  styleUrl: './dac.component.scss',
})
export class DacComponent implements OnInit {
  dacRepository = inject(DacRepository);
  private dacService = inject(DacService);
  private dynaDocsModelsService = inject(DynaDocsModelsService);
  private messageService = inject(MessageService);
  private authService = inject(AuthorizationService);
  router = inject(Router);

  @Input() aoId?: Observable<number>;
  @Input() couldCreateDac?: Observable<boolean>;

  dynaDocsModelOptions = new BehaviorSubject<SelectOption[]>([]);

  loading = true;

  @ViewChild('dialog') dialogComponent!: DialogComponent;
  dacDialogObject?: DialogObject = getFormBuilderObjectFactory();

  rowActionsFn: (target: DacResponse) => DataTableRowActions = (target: DacResponse) => {
    const additionalActions: Record<string, DataTableAction[] | undefined> = {};
    if (target.processInstanceId && target.status != TasksStatus.PUBLIEE)
      additionalActions['workflow'] = [
        {
          icon: 'pi pi-check',
          label: 'dac.actions.treat',
          action: () => {
            this.router.navigate([`dac-create`, target.dacId, target.dacVer]);
          },
        },
      ];

    if (target.couldSubmitBiddersList) {
      if (!additionalActions['workflow']) {
        additionalActions['workflow'] = [];
      }
      additionalActions['workflow'].push({
        icon: 'pi pi-upload',
        label: 'dac.actions.submitBidders',
        action: () => {
          this.openSubmitBiddersDialog(target);
        },
      });
    }
    additionalActions['history'] = [
      // {
      //   icon: 'pi pi-search',
      //   label: 'dac.actions.access_history',
      //   action: () => {
      //     this.router.navigate([`dac-version-history`, target.dacId]);
      //   },
      // },
    ];
    if (target.processInstanceId)
      additionalActions['history'].push({
        icon: 'pi pi-search',
        label: 'dac.actions.access_task_history',
        action: () => {
          this.router.navigate([`dac-task-history`, target.processInstanceId]);
        },
      });
    return {
      defaultActions: {
        inspect: {
          label: 'dac.actions.inspect',
        },
        edit: {
          label: 'dac.actions.modify',
        },
        // delete: {
        //   label: 'dac.actions.delete',
        // },
      },
      inspectAction:
        target.status !== TasksStatus.BROUILLON
          ? (row: DacResponse) => {
              this.router.navigate([`/dac-inspect`, row.dacId, row.dacVer]);
            }
          : undefined,
      editAction:
        target.status === TasksStatus.BROUILLON && this.authService.isRedacteur()
          ? (row: DacResponse) => {
              this.router.navigate([`/dac-create`, row.dacId, row.dacVer]);
            }
          : undefined,
      additionalActions,
    };
  };

  data: DacResponse[] = [];
  datatableObject: DynamicDataTable = getDatatableBuilderObjectFactory();

  ngOnInit(): void {
    this.loadData();
    this.aoId?.subscribe(id => this.dacRepository.setNested('dacCreateDto.appelOffreId', id));
    this.datatableObject = getDacDataTableStructure({
      rowActionsFn: this.rowActionsFn,
      createAction: this.authService.isRedacteur()
        ? () => {
            this.dialogComponent.toggleVisible();
            this.loadPreCreateData();
          }
        : undefined,
      createActionDisabled: this.couldCreateDac?.pipe(map(couldCreateDac => !couldCreateDac)),
    });
  }

  loadData() {
    this.loading = true;
    this.aoId?.subscribe(id => {
      if (id)
        this.dacService.getDacsByAo(id).subscribe({
          next: response => {
            this.data = response.content;
            this.datatableObject.data = this.data;
            this.loading = false;
          },
          error: error => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error loading DACs',
              detail: error.message,
            });
            this.loading = false;
          },
        });
    });
  }

  createDac() {
    const dto = this.dacRepository.get<DacCreateDto>('dacCreateDto');
    const result = dacPreCreateDraftValidationSchema().safeParse(dto);
    if (result.success) {
      this.dacService.createDacFromAo(dto).subscribe({
        next: response => {
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: 'DAC créé avec succès',
          });
          this.router.navigate(['dac-create', response.dacId, response.dacVer]);
          this.dialogComponent.toggleVisible();
        },
        error: error => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: error.error.error,
          });
        },
      });
    } else {
      this.dacRepository.setErrors(result.error.flatten().fieldErrors);
    }
  }

  loadPreCreateData() {
    const formObject = getDACPreCreateFormObject({
      store: this.dacRepository,
      dynaDocsModelOptions: this.dynaDocsModelOptions,
    });
    this.aoId?.subscribe(id => {
      if (id)
        this.dynaDocsModelsService.getListByAo(id).subscribe({
          next: data => {
            this.dynaDocsModelOptions.next(
              data.map(model => ({
                name: model.description,
                code: model.dynaDocsModelId,
              }))
            );

            this.dacDialogObject = getDacPreCreateDialogObject({
              formObject,
              action: () => this.createDac(),
              toggleVisiblity: () => {
                this.dialogComponent.toggleVisible();
              },
            });
          },
          error: error => {
            console.log(error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error loading data',
              detail: error.message,
            });
          },
        });
    });
  }

  openSubmitBiddersDialog(target: DacResponse) {
    this.dacDialogObject = {
      title: 'dac.actions.submitBidders',
      width: '850px',
      component: {
        outlet: SubmitBiddersDialogComponent,
        props: {
          aoId: target.aoId,
          onSaveSuccess: () => {
            this.dialogComponent.toggleVisible();
            this.loadData();
          },
          onClose: () => {
            this.dialogComponent.toggleVisible();
          }
        }
      },
      actions: []
    };
    this.dialogComponent.toggleVisible();
  }

  onDacCreateCancel() {
    console.log('DAC creation canceled');
  }
}
